import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "@/hooks/useTranslation";
import {
  isSeliarisOwnerNotFound,
  SeliarisOwnerStillRefusedError,
} from "@/lib/seliaris-errors";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

/**
 * Performs one CRM send, without an owner when asked to.
 *
 * @param withoutOwner True once the sender agreed to send the record with no
 *   Seliaris owner.
 */
export type OwnerChoiceSend<T> = (withoutOwner: boolean) => Promise<T>;

/** What the caller adds to a send asking about its owner. */
export type OwnerChoiceOptions = {
  /** Runs just before the question shows — to close the menu it came from. */
  onAsk?: () => void;
  /**
   * True when the send may create the record's company with it: the company
   * then goes without an owner too, and the question says so.
   */
  mayCreateCompany?: boolean;
};

/** The question as the dialog asks it. */
type OwnerQuestion = { recordName: string; mayCreateCompany: boolean };

/**
 * Sends a record to Seliaris and, when Seliaris knows no user with the
 * sender's email, asks them whether to send it without an owner or not at all.
 *
 * The question is asked per record: each send that hits the refusal opens its
 * own dialog, and the other sends of the page are not held up by it. Nothing
 * is retried on cancellation. A preceding successful company write is not
 * rolled back when Seliaris refuses the contact afterwards.
 *
 * @returns `sendWithOwnerChoice`, which resolves with the send's result, or
 *   null when the sender cancelled; a send without an owner that Seliaris
 *   still refuses over its owner rejects with `SeliarisOwnerStillRefusedError`,
 *   and every other failure rejects as it would have without it.
 *   `ownerDialog` is the dialog, to render in the caller's tree.
 */
export function useSeliarisOwnerChoice(): {
  sendWithOwnerChoice: <T>(
    recordName: string,
    send: OwnerChoiceSend<T>,
    options?: OwnerChoiceOptions,
  ) => Promise<T | null>;
  ownerDialog: ReactNode;
} {
  const { t } = useTranslation();
  const [question, setQuestion] = useState<OwnerQuestion | null>(null);
  // Held in a ref so that both the button and the dialog closing settle the
  // same question, and only once.
  const settleRef = useRef<((isConfirmed: boolean) => void) | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      settleRef.current?.(false);
      settleRef.current = null;
    };
  }, []);

  const askWithoutOwner = useCallback(
    (asked: OwnerQuestion) =>
      new Promise<boolean>((resolve) => {
        // A question still open is answered "cancel" rather than left hanging:
        // its send would otherwise never settle.
        settleRef.current?.(false);
        settleRef.current = resolve;
        setQuestion(asked);
      }),
    [],
  );

  const settle = (isConfirmed: boolean) => {
    settleRef.current?.(isConfirmed);
    settleRef.current = null;
    setQuestion(null);
  };

  const sendWithOwnerChoice = useCallback(
    async <T,>(
      name: string,
      send: OwnerChoiceSend<T>,
      { onAsk, mayCreateCompany = false }: OwnerChoiceOptions = {},
    ): Promise<T | null> => {
      try {
        return await send(false);
      } catch (error) {
        if (!isSeliarisOwnerNotFound(error)) throw error;
      }

      if (!isMountedRef.current) return null;
      onAsk?.();
      const isConfirmed = await askWithoutOwner({
        recordName: name,
        mayCreateCompany,
      });
      if (!isConfirmed) return null;
      try {
        return await send(true);
      } catch (error) {
        // The message for the first refusal tells the sender their address
        // matches no Seliaris user — true, but not why a send without an owner
        // failed. It gets its own, and the question is not asked again.
        if (isSeliarisOwnerNotFound(error)) {
          throw new SeliarisOwnerStillRefusedError(error);
        }
        throw error;
      }
    },
    [askWithoutOwner],
  );

  const ownerDialog = (
    <AlertDialog
      open={question !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) settle(false);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("team.integrations.seliaris.ownerDialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {/* A record with nothing to call it by is not quoted as « ». */}
            {question?.recordName.trim()
              ? t("team.integrations.seliaris.ownerDialog.description", {
                  record: question.recordName,
                })
              : t("team.integrations.seliaris.ownerDialog.descriptionUnnamed")}
            {question?.mayCreateCompany &&
              ` ${t("team.integrations.seliaris.ownerDialog.companyToo")}`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>
            {t("team.integrations.seliaris.ownerDialog.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => settle(true)}>
            {t("team.integrations.seliaris.ownerDialog.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { sendWithOwnerChoice, ownerDialog };
}
